import React, { useState, useEffect } from "react";
import Typography from "@mui/material/Typography";
import { Button, Grid, Container } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import { Helmet } from "react-helmet";
import { isMobile } from "react-device-detect";
import CustomCalendar from "./CalendarioPerfil";
import logoImage from "../../../assets/images/logo.png";
import Loader from "../../../components/Loader/Loader";

// Componente HourSelector inline
const HourSelector = ({ availableHours, onSelectHour }) => {
  const [selectedHour, setSelectedHour] = useState(null);

  const handleHourClick = (hour) => {
    setSelectedHour(hour);
    onSelectHour(hour);
  };

  // Separar horas en mañana y tarde
  const morningHours = availableHours.filter(hour => {
    const hourNum = parseInt(hour.split(':')[0]);
    return hourNum < 12;
  });

  const afternoonHours = availableHours.filter(hour => {
    const hourNum = parseInt(hour.split(':')[0]);
    return hourNum >= 12;
  });

  // Calcular el ancho disponible del contenedor
  const getContainerWidth = () => {
    if (isMobile) {
      return window.innerWidth * 0.94; // 94% menos padding del Box (2*16px)
    } else {
      return 800 * 0.7; // 800px menos 15% cada lado, menos padding del Box (2*32px)
    }
  };

// Calcular ancho uniforme basado en la fila con más elementos
const calculateUniformWidth = () => {
  const maxElementsPerRow = isMobile ? 4 : 5;
  const morningNeedsMultipleRows = morningHours.length >= maxElementsPerRow;
  const afternoonNeedsMultipleRows = afternoonHours.length >= maxElementsPerRow;
  
  if (!morningNeedsMultipleRows && !afternoonNeedsMultipleRows) {
    return "90px";
  }
  
  // Calcular ancho disponible del contenedor
  const containerWidth = isMobile ? 
    (window.innerWidth * 0.94 - 12) : // Mobile: 94% menos padding
    (800 * 0.7); // Desktop: 800px menos 15% cada lado y padding
  
  const gap = 4; // 0.5 * 8px
  const elementsInFullRow = maxElementsPerRow;
  
  // Calcular ancho por elemento para que la fila completa ocupe todo el ancho
  const uniformWidth = (containerWidth - (gap * (elementsInFullRow - 1))) / elementsInFullRow;
  
  return `${Math.floor(uniformWidth)}px`;
};

const uniformWidth = calculateUniformWidth();

const renderHourButton = (hour) => (
  <Button
    key={hour}
    onClick={() => handleHourClick(hour)}
    disableRipple
    disableElevation
    disableFocusRipple
    sx={{
      minWidth: uniformWidth,
      width: uniformWidth,
      height: "40px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
      color: selectedHour === hour ? "white" : "#191C1F",
      border: selectedHour === hour ? "none" : "1px solid #d0d0d0",
      fontFamily: selectedHour === hour ? "Quicksand-Bold" : "Quicksand-Medium",
      fontSize: { xs: "14px", md: "16px" },
      borderRadius: 2,
      boxShadow: "none !important",
      transition: "none !important",
      textTransform: "none",
      "&:hover": { 
        backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
        boxShadow: "none !important",
      },
      "&:active": {
        backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
        boxShadow: "none !important",
      },
      "&:focus": {
        backgroundColor: selectedHour === hour ? "#3B82F6" : "transparent",
        boxShadow: "none !important",
      },
      "& .MuiTouchRipple-root": {
        display: "none",
      },
    }}
  >
    {hour}
  </Button>
);

  return (
    <Box>
      {morningHours.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "15px", md: "16px" },
              color: "#626262",
              mb: 1,
            }}
          >
            Horarios AM:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {morningHours.map(hour => renderHourButton(hour))}
          </Box>
        </Box>
      )}
      
      {afternoonHours.length > 0 && (
        <Box>
          <Typography
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "15px", md: "16px" },
              color: "#626262",
              mb: 1,
            }}
          >
            Horarios PM:
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.5,
            }}
          >
            {afternoonHours.map(hour => renderHourButton(hour))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default function Calendario() {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedHour, setSelectedHour] = useState(null);
  const [availableHours, setAvailableHours] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const pageTitle = "Clinifica";
  const pageDescription =
    "Kinesiología: Tratamientos integrales para tus lesiones, en la comodidad de tu hogar.";
  const handleDateChange = (date, hours) => {
    setSelectedDate(date);
    setAvailableHours(hours);
    setSelectedHour(null);
  };

  const handleSelectHour = (hour) => {
    setSelectedHour(hour);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString + "T00:00:00");

    const days = [
      "domingo",
      "lunes",
      "martes",
      "miércoles",
      "jueves",
      "viernes",
      "sábado",
    ];
    const months = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    const dayName = days[date.getDay()];
    const day = date.getDate().toString().padStart(2, "0");
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${dayName} ${day} de ${month} ${year}`;
  };
  const fecha_formateada = formatDate(selectedDate);

  const handleAgendarClick = () => {
    navigate("/pro/perfil/datos", {
      state: {
        ...location.state,
        dates: selectedDate,
        hourss: selectedHour,
        fecha_formateada: fecha_formateada,
      },
    });
  };

  function renderButtomAfter() {
    if (selectedHour == null || selectedDate == null) {
      return (
        <Button
          disabled
          disableRipple
          disableElevation
          disableFocusRipple
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "14px", md: "16px" },
            backgroundColor: "#cccccc",
            color: "#666666",
            borderRadius: 2,
            boxShadow: "none !important",
            transition: "none !important",
            "&:hover": { 
              backgroundColor: "#cccccc",
              boxShadow: "none !important",
            },
            "&:active": {
              backgroundColor: "#cccccc",
              boxShadow: "none !important",
            },
            "&:focus": {
              backgroundColor: "#cccccc",
              boxShadow: "none !important",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
          }}
          variant="contained"
        >
          Continuar
        </Button>
      );
    } else {
      return (
        <Button
          disableRipple
          disableElevation
          disableFocusRipple
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "14px", md: "16px" },
            backgroundColor: "#3B82F6",
            borderRadius: 2,
            boxShadow: "none !important",
            transition: "none !important",
            "&:hover": { 
              backgroundColor: "#3B82F6",
              boxShadow: "none !important",
            },
            "&:active": {
              backgroundColor: "#3B82F6",
              boxShadow: "none !important",
            },
            "&:focus": {
              backgroundColor: "#3B82F6",
              boxShadow: "none !important",
            },
            "& .MuiTouchRipple-root": {
              display: "none",
            },
          }}
          onClick={handleAgendarClick}
          variant="contained"
        >
          Continuar
        </Button>
      );
    }
  }

  const textseciones = "✨Selecciona el día y hora de tu sesión.";



  // Scroll to top al cargar la página
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGoBack = () => {
    navigate(-1);
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  return (
    <>
      <Loader isLoading={isLoading} />
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link
          rel="canonical"
          href="https://www.Clinifica.com/kinesiologia-a-domicilio/agenda-inteligente/calendario"
        />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta
          property="og:url"
          content="https://www.Clinifica.com/kinesiologia-a-domicilio/agenda-inteligente/calendario"
        />
        <meta property="og:type" content="article" />
        <meta name="robots" content="noindex" />
      </Helmet>

      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />

      {/* Contenedor de información del servicio - ancho estándar morado */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262", 
          fontSize: { xs: "14px", md: "16px" }
        }}>
          Estas agendando con:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
          }}
        >
          {location.state.titulo}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { xs: "14px", md: "16px" }
          }}
        >
          {location.state.profesion}
        </Typography>
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262", 
          mt: 2,
          fontSize: { xs: "14px", md: "16px" }
        }}>
          El servicio de:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
          }}
        >
          {location.state.servicio_nombre}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { xs: "14px", md: "16px" }
          }}
        >
          {location.state.servicio_precios}
        </Typography>
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#f0f4ff",
            color: "#3B82F6",
            fontFamily: "Quicksand-Medium",
            fontSize: { xs: "13px", md: "15px" },
            borderRadius: 2,
            paddingLeft: 0.75,
            paddingRight: 0.75,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            ml: -0.75,
            mt: -0.75,    
            textAlign: "left",
          }}
        >
          {transformModalidad(location.state.servicio_modalidad)}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { xs: "20px", md: "24px" }, // ← Igual que A
          textAlign: "center",
          color: "#191C1F",                    // ← Negro como A
          mt: 3,
          mb: 1                                // ← Agregar mb como A
        }}
      >
        ¿Cuándo necesitas tu atención?
      </Typography>

      {/* Contenedor principal del calendario - ancho estándar blanco */}
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          backgroundColor: "white",
          borderRadius: 2,
          boxShadow: 1,
          mt: 2,
          mb: 5,
          p: isMobile ? 2 : 4,
        }}
      >
        {/* Wrapper con padding lateral del 15% para contenido principal */}
        <Box
          sx={{
            px: { xs: 0, md: "15%" },
          }}
        >
          <Typography
            sx={{
              mb: 3,
              fontFamily: "Quicksand-Regular",
              fontSize: { md: "16px", xs: "14px" },
              color: "#191C1F",
              textAlign: "center",
            }}
          >
            {textseciones}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: "100%",
                mt: 2,
              }}
            >
              <Box
                sx={{
                  borderRadius: 3,
                  backgroundColor: "white",
                  borderColor: "#d0d0d0",
                  borderWidth: 1,
                  borderStyle: "solid",
                  overflow: "hidden",
                  p: 2,
                }}
              >
                <CustomCalendar
                  disponibilidad={location.state.disponibilidad}
                  onDateChange={handleDateChange}
                />
                <Box
                  sx={{
                    mt: 2,
                    pb: 2,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: { xs: "10px", md: "16px" },
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "45px",
                      height: "15px",
                      borderRadius: 1,
                      backgroundColor: "#EEF5FF",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Bold",
                      fontSize: { xs: "12px", md: "14px" },
                    }}
                  >
                    Disponible
                  </Typography>
                  <Box
                    sx={{
                      display: "inline-block",
                      width: "45px",
                      height: "15px",
                      borderRadius: 1,
                      backgroundColor: "#3B82F6",
                    }}
                  />
                  <Typography
                    sx={{
                      fontFamily: "Quicksand-Bold",
                      fontSize: { xs: "12px", md: "14px" },
                    }}
                  >
                    Seleccionado
                  </Typography>
                </Box>
              </Box>
              <Typography
                sx={{
                  mt: 2,
                  fontFamily: "Quicksand-Regular",
                  color: "#626262",
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                <span
                  style={{ fontFamily: "Quicksand-Bold", color: "#3B82F6" }}
                >
                  {selectedDate ? formatDate(selectedDate) : ""}
                </span>
              </Typography>
              <Typography
                sx={{
                  mt: 1,
                  fontFamily: "Quicksand-Regular",
                  color: "#626262",
                  fontSize: { xs: "14px", md: "16px" },
                }}
              >
                Horarios disponibles:
              </Typography>
              <Box sx={{ mt: 1 }}>
                <HourSelector
                  availableHours={availableHours}
                  onSelectHour={handleSelectHour}
                />
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 3,
              mb: 2,
            }}
          >
            {renderButtomAfter()}
          </Box>
        </Box>
        {/* Botón Volver fuera del wrapper para mantener su posición original */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            mb: 3,
          }}
        >
          <Button
            onClick={handleGoBack}
            sx={{
              fontFamily: "Quicksand-Bold",
              fontSize: { xs: "14px", md: "16px" },
              color: "#007FFF",
              "&:hover": { backgroundColor: "rgba(0, 127, 255, 0.1)" },
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Volver
          </Button>
        </Box>
      </Box>
    </>
  );
}

